import { Filter, RefreshCw, Sparkles, X } from "lucide-react"
import { useState } from "react"
import { Configure, Index } from "react-instantsearch"
import { SearchPostHogClient } from "../../search-standalone/services/SearchPostHogClient"
import { Product } from "../../types/search"
import { cn } from "../../utils/cn"
import { CollectionResults } from "./CollectionResults"
import { EnhancedSearchBox, SearchType } from "./EnhancedSearchBox"
import { Filters, MobileFiltersDrawer } from "./Filters"
import { ProductCard } from "./ProductCard"
import { useSearchClient } from "./SearchProvider"
import { SearchResultsError, SearchResultsSkeleton } from "./SearchResults"

interface SearchPageProps {
	organizationId: string
	userId?: string
	onProductClick?: (product: Product) => void
	className?: string
}

/**
 * SearchPage is the main search interface component
 * Combines all search components into a complete search experience
 *
 * Layout:
 * - Mobile: Stack layout with bottom drawer for filters
 * - Tablet: 2-column grid
 * - Desktop: 3-column with sidebar filters
 */
export const SearchPage = ({ organizationId, onProductClick, className }: SearchPageProps) => {
	const { analyticsContext, buildAnalyticsProperties, selectedCollections, refreshCollections } = useSearchClient()
	const searchPostHog = SearchPostHogClient.getInstance()
	const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false)
	const [searchMode, setSearchMode] = useState<SearchType>("hybrid")
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [isRefreshing, setIsRefreshing] = useState(false)
	const [findSimilarTarget, setFindSimilarTarget] = useState<{ id: string; name: string } | null>(null)
	const [similarProducts, setSimilarProducts] = useState<Product[]>([])
	const [isFindingSimilar, setIsFindingSimilar] = useState(false)

	const handleProductClick = (product: Product) => {
		// Navigate to product page or open modal
		if (onProductClick) {
			onProductClick(product)
		} else {
			// Default behavior: open product URL in new tab
			if (product.url) {
				window.open(product.url, "_blank", "noopener,noreferrer")
			}
		}
	}

	const handleRetry = () => {
		setError(null)
		setIsLoading(true)
		// Retry logic would go here
		setTimeout(() => setIsLoading(false), 1000)
	}

	const handleRefreshCollections = async () => {
		setIsRefreshing(true)
		try {
			await refreshCollections()
			searchPostHog.capture(
				"search_collections_refreshed",
				buildAnalyticsProperties({
					collection_count: selectedCollections.length,
					collection_names: selectedCollections.map((collection) => collection.name),
				}),
				analyticsContext.externalSessionId,
			)
		} catch (err) {
			console.error("Failed to refresh collections:", err)
		} finally {
			setIsRefreshing(false)
		}
	}

	const handleFindSimilar = async (product: Product) => {
		const startedAt = performance.now()
		const collectionName = selectedCollections[0]?.name || "products"
		setFindSimilarTarget({ id: product.id, name: product.name })
		setIsFindingSimilar(true)
		setSimilarProducts([])
		window.scrollTo({ top: 0, behavior: "smooth" })
		searchPostHog.capture(
			"search_find_similar_clicked",
			buildAnalyticsProperties({
				...searchPostHog.buildSafeProductProperties(product),
				collection_name: collectionName,
			}),
			analyticsContext.externalSessionId,
		)

		try {
			const backendUrl = import.meta.env.VITE_BACKEND_URL || window.location.origin
			const response = await fetch(`${backendUrl}/api/v1/search/similar`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					productId: product.id,
					collectionName,
					organizationId: organizationId,
					per_page: 20,
				}),
			})

			if (!response.ok) {
				throw new Error(`Find similar failed: ${response.statusText}`)
			}

			const data = await response.json()
			console.log("[SearchPage] Find similar response:", data)

			// Extract products from Typesense response
			const products = data.results?.hits?.map((hit: any) => hit.document) || []
			setSimilarProducts(products)
			searchPostHog.capture(
				"search_find_similar_completed",
				buildAnalyticsProperties({
					...searchPostHog.buildSafeProductProperties(product),
					collection_name: collectionName,
					result_count: products.length,
					latency_ms: Math.round(performance.now() - startedAt),
				}),
				analyticsContext.externalSessionId,
			)
		} catch (error) {
			console.error("[SearchPage] Find similar error:", error)
			searchPostHog.capture(
				"search_find_similar_failed",
				buildAnalyticsProperties({
					...searchPostHog.buildSafeProductProperties(product),
					...searchPostHog.normalizeError(error),
					collection_name: collectionName,
					latency_ms: Math.round(performance.now() - startedAt),
				}),
				analyticsContext.externalSessionId,
			)
			setError(error instanceof Error ? error.message : "Failed to find similar products")
		} finally {
			setIsFindingSimilar(false)
		}
	}

	const handleClearFindSimilar = () => {
		setFindSimilarTarget(null)
		setSimilarProducts([])
	}

	return (
		<div className={cn("flex flex-col h-full bg-background", className)}>
			{/* Configure InstantSearch - only when not in "Find Similar" mode */}
			{!findSimilarTarget && (
				<Configure
					analytics={false}
					analyticsTags={searchMode !== "keyword" ? ["semantic"] : []}
					attributesToSnippet={["description:50"]}
					hitsPerPage={20}
					snippetEllipsisText="..."
				/>
			)}

			{/* Header with Search Box */}
			<div className="sticky top-0 z-20 bg-background border-b border-border">
				<div className="container mx-auto px-4 py-4">
					<div className="flex flex-col gap-3">
						<div className="flex items-center gap-4">
							{/* Mobile Filter Button */}
							<button
								className="lg:hidden flex items-center gap-2 px-3 py-2 rounded-md border border-input hover:bg-accent transition-colors"
								onClick={() => {
									searchPostHog.capture(
										"search_filters_opened_mobile",
										buildAnalyticsProperties({
											collection_count: selectedCollections.length,
											collection_names: selectedCollections.map((collection) => collection.name),
										}),
										analyticsContext.externalSessionId,
									)
									setIsMobileFiltersOpen(true)
								}}>
								<Filter className="w-4 h-4" />
								<span className="text-sm font-medium">Filters</span>
							</button>

							{/* Search Box */}
							<EnhancedSearchBox
								className="flex-1"
								defaultMode={searchMode}
								onSearchModeChange={setSearchMode}
								showSearchTypeToggle
							/>
						</div>

						{/* Find Similar Banner */}
						{findSimilarTarget && (
							<div className="flex items-center justify-between px-3 py-2 bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800 rounded-md text-sm animate-in fade-in slide-in-from-top-2 duration-200">
								<div className="flex items-center gap-2 text-purple-700 dark:text-purple-300">
									<Sparkles className="w-4 h-4" />
									<span>
										Finding similar to <span className="font-semibold">{findSimilarTarget.name}</span>
									</span>
								</div>
								<button
									className="p-1 hover:bg-purple-100 dark:hover:bg-purple-800 rounded-full transition-colors text-purple-600 dark:text-purple-400"
									onClick={handleClearFindSimilar}
									title="Clear similarity search">
									<X className="w-4 h-4" />
								</button>
							</div>
						)}

						{/* Collection Info Bar */}
						{selectedCollections.length > 0 && (
							<div className="flex items-center justify-between px-2 py-1.5 bg-muted/50 rounded-md text-xs">
								<div className="flex items-center gap-2">
									<span className="text-muted-foreground">Searching in:</span>
									<div className="flex items-center gap-1.5">
										{selectedCollections.map((collection, index) => (
											<span key={collection.name}>
												<span className="font-medium text-foreground">{collection.name}</span>
												{index < selectedCollections.length - 1 && (
													<span className="text-muted-foreground mx-1">•</span>
												)}
											</span>
										))}
									</div>
								</div>
								<button
									className="flex items-center gap-1.5 px-2 py-1 rounded hover:bg-accent transition-colors disabled:opacity-50"
									disabled={isRefreshing}
									onClick={handleRefreshCollections}
									title="Refresh collection selection">
									<RefreshCw className={cn("w-3 h-3", isRefreshing && "animate-spin")} />
									<span className="text-muted-foreground">Refresh</span>
								</button>
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Main Content */}
			<div className="flex-1 overflow-hidden">
				<div className="container mx-auto px-4 py-6 h-full">
					<div className="flex gap-6 h-full">
						{/* Desktop Filters Sidebar */}
						<aside className="hidden lg:block w-64 flex-shrink-0">
							<div className="sticky top-24">
								<Filters />
							</div>
						</aside>

						{/* Results Area */}
						<main className="flex-1 min-w-0 overflow-y-auto no-scrollbar">
							<div className="flex flex-col gap-6 pb-20">
								{/* Results - Conditional rendering based on mode */}
								{error ? (
									<SearchResultsError error={error} onRetry={handleRetry} />
								) : findSimilarTarget ? (
									/* Similar Products Results */
									<div className="mb-8">
										<div className="flex items-center justify-between mb-4 pb-2 border-b border-border">
											<div className="flex items-center gap-2">
												<Sparkles className="w-5 h-5 text-purple-500" />
												<h3 className="text-lg font-semibold">Similar Products</h3>
											</div>
											<div className="text-sm text-muted-foreground">
												{isFindingSimilar ? (
													<span className="flex items-center gap-2">
														<div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
														Finding similar...
													</span>
												) : (
													<span>
														{similarProducts.length} result{similarProducts.length !== 1 ? "s" : ""}
													</span>
												)}
											</div>
										</div>

										{isFindingSimilar ? (
											<SearchResultsSkeleton />
										) : similarProducts.length > 0 ? (
											<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
												{similarProducts.map((product) => (
													<ProductCard
														hasSemanticSearch={true}
														key={product.id}
														onClick={handleProductClick}
														onFindSimilar={handleFindSimilar}
														product={product}
													/>
												))}
											</div>
										) : (
											<div className="flex items-center justify-center p-12 text-center">
												<div className="max-w-md">
													<p className="text-muted-foreground mb-2">No similar products found</p>
													<p className="text-sm text-muted-foreground">
														Try selecting a different product or clear the search to browse all
														products.
													</p>
												</div>
											</div>
										)}
									</div>
								) : isLoading ? (
									<SearchResultsSkeleton />
								) : selectedCollections.length > 0 ? (
									<>
										{/* Render results for each selected collection */}
										{selectedCollections.map((collection) => (
											<Index indexName={collection.name} key={collection.name}>
												<CollectionResults
													collectionName={collection.name}
													hasSemanticSearch={collection.searchCapabilities.semantic}
													onFindSimilar={handleFindSimilar}
													onProductClick={handleProductClick}
												/>
											</Index>
										))}
									</>
								) : (
									<div className="flex items-center justify-center p-12 text-center">
										<div className="max-w-md">
											<p className="text-muted-foreground mb-2">No collections selected</p>
											<p className="text-sm text-muted-foreground">
												Please select at least one collection in settings to start searching.
											</p>
										</div>
									</div>
								)}
							</div>
						</main>
					</div>
				</div>
			</div>

			{/* Mobile Filters Drawer */}
			<MobileFiltersDrawer isOpen={isMobileFiltersOpen} onClose={() => setIsMobileFiltersOpen(false)} />
		</div>
	)
}
